<template>
  <div class="team-page">
    <TopBarTitleComponent title="Equipo" :showBackIcon="true">
      <template slot="right-area">
        <div class="d-flex justify-content-end align-items-center">
          <ButtonPrimaryComponent
            v-if="$can('team.manage')"
            title="Invitar miembro"
            @onSaveClick="openInvite"
          />
        </div>
      </template>
    </TopBarTitleComponent>

    <b-container fluid class="pb-4 px-lg-4 pt-4">
      <!-- Cupo del plan -->
      <section v-if="limits && limits.users_limit" class="team-seats">
        <div class="team-seats__head">
          <div class="team-seats__text">
            <span class="team-seats__count"
              >{{ seatsUsed }} de {{ limits.users_limit }}</span
            >
            usuarios de tu plan
            <span v-if="limits.pending" class="team-seats__pending"
              >· {{ limits.pending }} invitación(es) pendiente(s)</span
            >
          </div>
          <router-link
            v-if="limits.can_buy_seats && $store.getters.myRole !== 'member'"
            :to="{ name: 'plans', hash: '#licencias' }"
            class="team-btn"
            :class="{ 'team-btn--accent': seatsUsed >= limits.users_limit }"
          >
            <i class="el-icon-plus"></i> Comprar licencias
          </router-link>
        </div>
        <p
          v-if="seatsUsed >= limits.users_limit && limits.can_buy_seats"
          class="team-seats__full"
        >
          Llegaste al límite de usuarios. Compra licencias adicionales para
          invitar a más personas.
        </p>
        <div class="team-seats__bar">
          <div
            class="team-seats__fill"
            :class="{ 'is-full': seatsUsed >= limits.users_limit }"
            :style="{
              width: Math.min(100, (seatsUsed / limits.users_limit) * 100) + '%'
            }"
          ></div>
        </div>
      </section>

      <!-- Miembros -->
      <section class="team-card" v-loading="loading">
        <header class="team-card__header">
          <h4 class="team-card__title">Miembros</h4>
          <span class="team-card__hint">{{ members.length }} en el equipo</span>
        </header>

        <div
          v-for="row in members"
          :key="row.id"
          class="team-row"
          :class="{ 'is-clickable': canEdit(row) }"
          @click="canEdit(row) && openEditDrawer(row)"
        >
          <div class="team-row__person">
            <el-avatar :size="40" :src="row.image" class="team-row__avatar">
              {{ (row.name || row.email || "").charAt(0).toUpperCase() }}
            </el-avatar>
            <div class="team-row__identity">
              <span class="team-row__name"
                >{{ row.name }} {{ row.last_name }}</span
              >
              <span class="team-row__email">{{ row.email }}</span>
            </div>
          </div>

          <span class="team-role" :class="'team-role--' + row.role">{{
            roleLabel(row.role)
          }}</span>

          <span class="team-row__meta">
            <template v-if="row.role === 'owner'">Acceso completo</template>
            <template v-else-if="row.role === 'admin'"
              >Todo, salvo lo negado</template
            >
            <template v-else>
              {{ row.permissions.length }} permisos ·
              {{ scopeLabel(row) }}
            </template>
          </span>

          <span class="team-row__meta">
            <template v-if="row.emission_points.length"
              >{{ row.emission_points.length }} punto(s) de emisión</template
            >
            <template
              v-else-if="
                row.role !== 'member' || row.permissions.includes('scope.all')
              "
              >Todos los puntos</template
            >
            <template v-else>Sin punto asignado</template>
          </span>

          <div class="team-row__actions" @click.stop>
            <el-dropdown
              v-if="canEdit(row)"
              trigger="click"
              @command="handleCommand($event, row)"
            >
              <button class="team-icon-btn" aria-label="Opciones">
                <i class="el-icon-more"></i>
              </button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit"
                  >Editar permisos</el-dropdown-item
                >
                <el-dropdown-item command="remove" divided>
                  <span class="text-danger">Remover del equipo</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </section>

      <!-- Invitaciones pendientes -->
      <section
        class="team-card mt-4"
        v-if="invitations.length && $can('team.manage')"
      >
        <header class="team-card__header">
          <h4 class="team-card__title">Invitaciones pendientes</h4>
          <span class="team-card__hint"
            >Aún no crean su cuenta. El enlace expira a los 7 días.</span
          >
        </header>

        <div v-for="row in invitations" :key="row.id" class="team-row">
          <div class="team-row__person">
            <el-avatar
              :size="40"
              class="team-row__avatar team-row__avatar--pending"
            >
              <i class="el-icon-message"></i>
            </el-avatar>
            <div class="team-row__identity">
              <span class="team-row__name">{{ row.email }}</span>
              <span class="team-row__email"
                >Expira el {{ formatDate(row.expires_at) }}</span
              >
            </div>
          </div>
          <span class="team-role" :class="'team-role--' + row.role">{{
            roleLabel(row.role)
          }}</span>
          <div class="team-row__buttons">
            <button class="team-btn" @click="copyLink(row.invite_url)">
              <i class="el-icon-link"></i> Copiar enlace
            </button>
            <button class="team-btn" @click="resendInvitation(row)">
              Reenviar
            </button>
            <button
              class="team-btn team-btn--danger"
              @click="cancelInvitation(row)"
            >
              Cancelar
            </button>
          </div>
        </div>
      </section>
    </b-container>

    <!-- Invitar miembro (pantalla completa, acciones en el encabezado) -->
    <el-dialog
      :visible.sync="showInviteModal"
      fullscreen
      :show-close="false"
      custom-class="team-dialog"
      @closed="resetInvite"
    >
      <!-- Encabezado propio: el global oculta el nativo en pantalla completa -->
      <div class="team-dialog__header">
        <h3 class="team-dialog__title">Invitar miembro</h3>
        <div class="team-dialog__actions">
          <button class="team-btn" @click="showInviteModal = false">
            {{ inviteResult ? "Cerrar" : "Cancelar" }}
          </button>
          <ButtonPrimaryComponent
            v-if="!inviteResult"
            title="Enviar invitación"
            :disabled="inviteLoading || !inviteForm.email"
            @onSaveClick="inviteMember"
          />
          <button
            class="team-dialog__close"
            aria-label="Cerrar"
            @click="showInviteModal = false"
          >
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <div class="team-dialog__body">
        <template v-if="!inviteResult">
          <el-form :model="inviteForm" label-position="top">
            <el-form-item label="Correo de la persona">
              <el-input
                v-model="inviteForm.email"
                placeholder="usuario@empresa.com"
                type="email"
              ></el-input>
              <p class="team-dialog__help">
                Si ya tiene cuenta en Mydessk, le damos acceso y le avisamos por
                correo; verá este negocio en "Cambiar negocio". Si no tiene,
                recibe un enlace para crear su propia cuenta y contraseña.
              </p>
            </el-form-item>
            <el-form-item label="Rol">
              <el-select v-model="inviteForm.role" style="width: 100%">
                <el-option
                  v-if="$store.getters.myRole === 'owner'"
                  label="Administrador (todo el negocio)"
                  value="admin"
                ></el-option>
                <el-option
                  label="Miembro (solo lo que le asignes)"
                  value="member"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item
              v-if="inviteForm.role === 'member'"
              label="Plantilla de permisos"
            >
              <el-select
                v-model="selectedTemplate"
                placeholder="Seleccionar plantilla"
                style="width: 100%"
                clearable
                @change="applyTemplate"
              >
                <el-option
                  v-for="t in templates"
                  :key="t.id"
                  :label="t.name"
                  :value="t.id"
                ></el-option>
              </el-select>
              <p class="team-dialog__help">
                Puedes ajustar los permisos uno a uno después de invitar.
              </p>
            </el-form-item>
          </el-form>
        </template>

        <div
          v-else
          class="team-result"
          :class="{ 'is-warning': !inviteResult.mail_sent }"
        >
          <i
            :class="
              inviteResult.mail_sent
                ? 'el-icon-circle-check'
                : 'el-icon-warning-outline'
            "
            class="team-result__icon"
          ></i>
          <p class="team-result__message">{{ inviteResult.message }}</p>
          <div v-if="inviteResult.invite_url" class="team-result__link">
            <el-input :value="inviteResult.invite_url" readonly></el-input>
            <button class="team-btn" @click="copyLink(inviteResult.invite_url)">
              <i class="el-icon-link"></i> Copiar enlace
            </button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Edit Permissions Drawer -->
    <el-drawer
      :title="'Permisos de ' + (editingMember ? editingMember.name : '')"
      :visible.sync="showEditDrawer"
      size="500px"
      direction="rtl"
    >
      <div class="px-4 pb-4" v-if="editingMember">
        <!-- Role selector -->
        <div class="mb-4">
          <label class="font-semi-bold d-block mb-2">Rol</label>
          <el-radio-group v-model="editForm.role" @change="updateRole">
            <el-radio-button label="admin">Administrador</el-radio-button>
            <el-radio-button label="member">Miembro</el-radio-button>
          </el-radio-group>
        </div>

        <!-- Admin: todo salvo lo que se apague -->
        <p v-if="editForm.role === 'admin'" class="team-dialog__help mb-4">
          Un administrador tiene acceso a todo el negocio. Apaga lo que quieras
          negarle; el resto queda permitido.
        </p>

        <!-- Template quick-apply -->
        <div class="mb-4">
          <label class="font-semi-bold d-block mb-2">Aplicar plantilla</label>
          <el-select
            v-model="selectedEditTemplate"
            placeholder="Seleccionar..."
            style="width: 100%"
            clearable
            @change="applyTemplateToEdit"
          >
            <el-option
              v-for="t in templates"
              :key="t.id"
              :label="t.name"
              :value="t.id"
            ></el-option>
          </el-select>
        </div>

        <!-- Permission toggles by module -->
        <div v-for="module in permissionModules" :key="module.key" class="mb-3">
          <div
            class="d-flex justify-content-between align-items-center cursor-pointer py-2 border-bottom"
            @click="toggleModule(module.key)"
          >
            <span class="font-semi-bold">{{ module.label }}</span>
            <div class="d-flex align-items-center">
              <el-switch
                :value="isModuleEnabled(module.key)"
                @change="toggleAllModulePermissions(module)"
                class="mr-2"
              ></el-switch>
              <i
                :class="
                  expandedModules.includes(module.key)
                    ? 'el-icon-arrow-up'
                    : 'el-icon-arrow-down'
                "
              ></i>
            </div>
          </div>
          <div v-show="expandedModules.includes(module.key)" class="pl-3 pt-2">
            <div
              v-for="perm in module.permissions"
              :key="perm.key"
              class="d-flex justify-content-between align-items-center py-1"
            >
              <span class="text-muted">{{ perm.label }}</span>
              <el-switch
                :value="editForm.permissions.includes(perm.key)"
                @change="togglePermission(perm.key)"
                size="small"
              ></el-switch>
            </div>
          </div>
        </div>

        <!-- Emission Points -->
        <div class="mb-3 mt-4">
          <label class="font-semi-bold d-block mb-2"
            >Puntos de emisión asignados</label
          >
          <el-checkbox-group v-model="editForm.emissionPointIds">
            <el-checkbox
              v-for="ep in allEmissionPoints"
              :key="ep.id"
              :label="ep.id"
              class="d-block mb-1"
            >
              {{ ep.code }} - {{ ep.description || "Sin descripción" }}
            </el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="mt-4">
          <el-button
            type="primary"
            :loading="saveLoading"
            @click="savePermissions"
            style="width: 100%"
          >
            Guardar cambios
          </el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import teamService from "../services/index";
import TopBarTitleComponent from "../../components/TopBarTitle";
import ButtonPrimaryComponent from "../../components/ButtonPrimary";

export default {
  name: "TeamList",
  components: { TopBarTitleComponent, ButtonPrimaryComponent },
  data() {
    return {
      inviteResult: null,
      loading: false,
      members: [],
      templates: [],
      allEmissionPoints: [],
      invitations: [],
      limits: null,
      showInviteModal: false,
      showEditDrawer: false,
      inviteLoading: false,
      saveLoading: false,
      editingMember: null,
      selectedTemplate: null,
      selectedEditTemplate: null,
      expandedModules: [],
      inviteForm: {
        email: "",
        role: "member",
        permissions: []
      },
      editForm: {
        role: "member",
        permissions: [],
        emissionPointIds: []
      },
      permissionModules: [
        {
          key: "sales_quotes",
          label: "Cotizaciones",
          permissions: [
            { key: "quotes.view", label: "Ver" },
            { key: "quotes.create", label: "Crear" },
            { key: "quotes.edit", label: "Editar" },
            { key: "quotes.convert", label: "Convertir a factura" }
          ]
        },
        {
          key: "sales_invoices",
          label: "Facturas",
          permissions: [
            { key: "invoices.view", label: "Ver" },
            { key: "invoices.create", label: "Crear" },
            { key: "invoices.edit", label: "Editar borrador" },
            { key: "invoices.void", label: "Anular" }
          ]
        },
        {
          key: "sales_credit_notes",
          label: "Notas de crédito",
          permissions: [
            { key: "credit_notes.view", label: "Ver" },
            { key: "credit_notes.create", label: "Crear" }
          ]
        },
        {
          key: "sales_debit_notes",
          label: "Notas de débito",
          permissions: [
            { key: "debit_notes.view", label: "Ver" },
            { key: "debit_notes.create", label: "Crear" }
          ]
        },
        {
          key: "sales_withholdings",
          label: "Retenciones",
          permissions: [
            { key: "withholdings.view", label: "Ver" },
            { key: "withholdings.create", label: "Crear" }
          ]
        },
        {
          key: "sales_liquidations",
          label: "Liquidaciones",
          permissions: [
            { key: "liquidations.view", label: "Ver" },
            { key: "liquidations.create", label: "Crear" }
          ]
        },
        {
          key: "sales_payments",
          label: "Pagos",
          permissions: [
            { key: "payments.view", label: "Ver" },
            { key: "payments.manage", label: "Registrar pagos" }
          ]
        },
        {
          key: "catalogue",
          label: "Catálogo",
          permissions: [
            { key: "products.view", label: "Ver productos" },
            { key: "products.manage", label: "Crear/editar productos" },
            { key: "categories.manage", label: "Gestionar categorías" },
            { key: "inventory.view", label: "Ver inventario" }
          ]
        },
        {
          key: "clients",
          label: "Directorio (Clientes)",
          permissions: [
            { key: "clients.view", label: "Ver" },
            { key: "clients.manage", label: "Crear/editar" }
          ]
        },
        {
          key: "reports",
          label: "Reportes",
          permissions: [{ key: "reports.view", label: "Ver reportes" }]
        },
        {
          key: "settings",
          label: "Configuraciones",
          permissions: [
            { key: "settings.view", label: "Ver" },
            { key: "settings.manage", label: "Modificar" },
            { key: "templates.manage", label: "Gestionar plantillas" }
          ]
        },
        {
          key: "contracts",
          label: "Contratos",
          permissions: [
            { key: "contracts.view", label: "Ver" },
            { key: "contracts.manage", label: "Crear/editar" }
          ]
        },
        {
          key: "wallet",
          label: "Wallet",
          permissions: [
            { key: "wallet.view", label: "Ver balance" },
            { key: "wallet.withdraw", label: "Solicitar retiros" }
          ]
        },
        {
          key: "team",
          label: "Equipo",
          permissions: [
            { key: "team.view", label: "Ver miembros" },
            { key: "team.manage", label: "Gestionar equipo" }
          ]
        },
        {
          key: "marketing_leads",
          label: "Marketing — Leads",
          permissions: [
            { key: "marketing.leads.view", label: "Ver leads" },
            {
              key: "marketing.leads.manage",
              label: "Crear/editar leads y formularios"
            }
          ]
        },
        {
          key: "marketing_messaging",
          label: "Marketing — Mensajería",
          permissions: [
            { key: "marketing.messaging.view", label: "Ver conversaciones" },
            {
              key: "marketing.messaging.send",
              label: "Enviar mensajes y plantillas"
            }
          ]
        },
        {
          key: "marketing_flows",
          label: "Marketing — Flujos",
          permissions: [
            { key: "marketing.flows.view", label: "Ver flujos" },
            { key: "marketing.flows.manage", label: "Crear/editar flujos IA" }
          ]
        },
        {
          key: "marketing_mailing",
          label: "Marketing — Mailing",
          permissions: [
            { key: "marketing.mailing.view", label: "Ver campañas" },
            { key: "marketing.mailing.manage", label: "Crear/enviar campañas" }
          ]
        },
        {
          key: "marketing_sites",
          label: "Marketing — Landings",
          permissions: [
            { key: "marketing.sites.view", label: "Ver landings" },
            { key: "marketing.sites.manage", label: "Crear/publicar landings" }
          ]
        },
        {
          key: "business",
          label: "Negocio",
          permissions: [
            { key: "business.profile", label: "Editar perfil" },
            {
              key: "emission_points.manage",
              label: "Gestionar puntos de emisión"
            },
            {
              key: "establishments.manage",
              label: "Gestionar establecimientos"
            },
            {
              key: "bank_accounts.manage",
              label: "Gestionar cuentas bancarias"
            }
          ]
        }
      ]
    };
  },
  computed: {
    seatsUsed() {
      return this.limits ? this.limits.active + this.limits.pending : 0;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [
          membersRes,
          templatesRes,
          epRes,
          invRes,
          catalogRes
        ] = await Promise.all([
          teamService.getTeamMembers(),
          teamService.getTemplates(),
          teamService.getEmissionPoints().catch(() => null),
          teamService.getInvitations().catch(() => null),
          teamService.getCatalog().catch(() => null)
        ]);
        // Catálogo de permisos del backend (fuente de verdad); la lista local
        // queda solo como respaldo si no responde.
        const catalog = catalogRes && catalogRes.data && catalogRes.data.data;
        if (catalog && Object.keys(catalog).length) {
          this.permissionModules = Object.entries(catalog).map(
            ([key, module]) => ({
              key,
              label: module.label,
              permissions: Object.entries(
                module.permissions || {}
              ).map(([permKey, label]) => ({ key: permKey, label }))
            })
          );
        }
        if (membersRes.data.success) {
          this.members = membersRes.data.data;
          this.limits = membersRes.data.limits || null;
        }
        if (templatesRes.data.success) this.templates = templatesRes.data.data;
        if (epRes && epRes.data && epRes.data.success) {
          this.allEmissionPoints = epRes.data.data || [];
        }
        if (invRes && invRes.data && invRes.data.success) {
          this.invitations = invRes.data.data || [];
        }
      } catch {
        // Error handled by interceptor
      }
      this.loading = false;
    },
    async resendInvitation(invitation) {
      try {
        const { data } = await teamService.resendInvitation(invitation.id);
        if (data.data && data.data.mail_sent) {
          this.$notifications.success({ message: "Invitación reenviada" });
        } else {
          this.$notifications.warning({
            message:
              'No se pudo enviar el correo. Usa "Copiar enlace" y compártelo.'
          });
        }
      } catch {
        // Error handled by interceptor
      }
    },
    cancelInvitation(invitation) {
      this.$confirm(
        `¿Cancelar la invitación a ${invitation.email}?`,
        "Confirmar",
        {
          confirmButtonText: "Sí, cancelar",
          cancelButtonText: "Volver",
          type: "warning"
        }
      )
        .then(async () => {
          try {
            await teamService.cancelInvitation(invitation.id);
            this.invitations = this.invitations.filter(
              i => i.id !== invitation.id
            );
            this.$notifications.success({ message: "Invitación cancelada" });
          } catch {
            // Error handled by interceptor
          }
        })
        .catch(() => {});
    },
    openInvite() {
      this.resetInvite();
      this.showInviteModal = true;
    },
    resetInvite() {
      this.inviteResult = null;
      this.selectedTemplate = null;
      this.inviteForm = { email: "", role: "member", permissions: [] };
    },
    // Nadie se edita a sí mismo ni al propietario; solo el propietario
    // gestiona administradores (misma regla que el backend).
    canEdit(row) {
      if (!this.$can("team.manage") || row.role === "owner") return false;
      const me = this.$store.getters.getUser || {};
      if (row.user_id === me.id) return false;
      if (row.role === "admin") return this.$store.getters.myRole === "owner";
      return true;
    },
    async copyLink(url) {
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        this.$notifications.success({ message: "Enlace copiado" });
      } catch {
        this.$notifications.warning({ message: url });
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("es-EC") : "";
    },
    // Alcance de un miembro: todo el negocio, todos los leads, o solo lo suyo.
    scopeLabel(row) {
      const p = row.permissions || [];
      if (p.includes("scope.all")) return "Supervisor";
      if (p.includes("marketing.leads.all")) return "Todos los leads";
      return "Solo lo suyo";
    },
    roleLabel(role) {
      const labels = {
        owner: "Propietario",
        admin: "Administrador",
        member: "Miembro"
      };
      return labels[role] || role;
    },
    roleTagType(role) {
      const types = { owner: "warning", admin: "primary", member: "" };
      return types[role] || "";
    },
    handleCommand(command, row) {
      if (command === "edit") this.openEditDrawer(row);
      if (command === "remove") this.confirmRemove(row);
    },
    openEditDrawer(member) {
      this.editingMember = member;
      this.editForm.role = member.role;
      this.editForm.permissions = [...member.permissions];
      this.editForm.emissionPointIds = member.emission_points.map(ep => ep.id);
      this.showEditDrawer = true;
    },
    toggleModule(key) {
      const idx = this.expandedModules.indexOf(key);
      if (idx >= 0) this.expandedModules.splice(idx, 1);
      else this.expandedModules.push(key);
    },
    isModuleEnabled(moduleKey) {
      const module = this.permissionModules.find(m => m.key === moduleKey);
      if (!module) return false;
      return module.permissions.some(p =>
        this.editForm.permissions.includes(p.key)
      );
    },
    toggleAllModulePermissions(module) {
      const allKeys = module.permissions.map(p => p.key);
      const allEnabled = allKeys.every(k =>
        this.editForm.permissions.includes(k)
      );
      if (allEnabled) {
        this.editForm.permissions = this.editForm.permissions.filter(
          p => !allKeys.includes(p)
        );
      } else {
        allKeys.forEach(k => {
          if (!this.editForm.permissions.includes(k))
            this.editForm.permissions.push(k);
        });
      }
    },
    togglePermission(key) {
      const idx = this.editForm.permissions.indexOf(key);
      if (idx >= 0) this.editForm.permissions.splice(idx, 1);
      else this.editForm.permissions.push(key);
    },
    applyTemplate(templateId) {
      const template = this.templates.find(t => t.id === templateId);
      if (template) this.inviteForm.permissions = [...template.permissions];
    },
    applyTemplateToEdit(templateId) {
      const template = this.templates.find(t => t.id === templateId);
      if (template) this.editForm.permissions = [...template.permissions];
    },
    async updateRole(role) {
      try {
        await teamService.updateRole(this.editingMember.user_id, { role });
        this.editingMember.role = role;
        this.$notifications.success({ message: "Rol actualizado" });
      } catch {
        // Error handled by interceptor
      }
    },
    async savePermissions() {
      this.saveLoading = true;
      try {
        await Promise.all([
          teamService.updatePermissions(this.editingMember.user_id, {
            permissions: this.editForm.permissions
          }),
          teamService.updateEmissionPoints(this.editingMember.user_id, {
            emission_point_ids: this.editForm.emissionPointIds
          })
        ]);
        this.$notifications.success({
          message: "Permisos actualizados correctamente"
        });
        this.showEditDrawer = false;
        this.loadData();
      } catch {
        // Error handled by interceptor
      }
      this.saveLoading = false;
    },
    async inviteMember() {
      if (!this.inviteForm.email) return;
      this.inviteLoading = true;
      try {
        const { data } = await teamService.inviteMember(this.inviteForm);
        const result = data.data || {};
        this.inviteResult = {
          message: data.message,
          mail_sent: result.mail_sent !== false,
          // El enlace solo hace falta si el correo no salió.
          invite_url: result.mail_sent === false ? result.invite_url : null
        };
        this.loadData();
      } catch {
        // Error handled by interceptor
      }
      this.inviteLoading = false;
    },
    confirmRemove(member) {
      this.$confirm(`¿Estás seguro de remover a ${member.name}?`, "Confirmar", {
        confirmButtonText: "Sí, remover",
        cancelButtonText: "Cancelar",
        type: "warning"
      })
        .then(async () => {
          try {
            await teamService.removeMember(member.user_id);
            this.$notifications.success({ message: "Miembro removido" });
            this.loadData();
          } catch {
            // Error handled by interceptor
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped lang="scss">
$primary: #2d2b55;
$accent: #3ecfb4;
$green: #1d9e75;
$border: #e6e8f0;
$muted: #8a8fa8;

.team-page {
  font-family: "Plus Jakarta Sans", sans-serif;
}

// ── Cupo del plan ──
.team-seats {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;

  &__text {
    color: $muted;
    font-size: 14px;
    margin-bottom: 10px;
  }

  &__count {
    color: $primary;
    font-weight: 700;
  }

  &__bar {
    height: 6px;
    border-radius: 999px;
    background: #eef0f6;
    overflow: hidden;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;

    .team-seats__text {
      margin-bottom: 0;
    }
  }

  &__full {
    margin: 0 0 10px;
    font-size: 13px;
    color: #b7791f;
  }

  &__fill {
    height: 100%;
    background: $accent;
    border-radius: 999px;
    transition: width 0.3s;

    &.is-full {
      background: #f5a623;
    }
  }
}

// ── Tarjetas ──
.team-card {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 10px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 16px 20px;
    border-bottom: 1px solid $border;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: $primary;
  }

  &__hint {
    font-size: 13px;
    color: $muted;
  }
}

// ── Filas ──
.team-row {
  display: grid;
  grid-template-columns:
    minmax(220px, 2fr) 130px minmax(140px, 1fr) minmax(140px, 1fr)
    48px;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f2f7;

  &:last-child {
    border-bottom: 0;
  }

  &.is-clickable {
    cursor: pointer;

    &:hover {
      background: #f9fafc;
    }
  }

  &__person {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  &__avatar {
    flex-shrink: 0;
    background: #eef0f6;
    color: $primary;
    font-weight: 600;

    &--pending {
      color: $muted;
    }
  }

  &__identity {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    color: $primary;
    font-weight: 600;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__email {
    color: $muted;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    color: #555a75;
    font-size: 13px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__buttons {
    grid-column: 3 / span 3;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.team-role {
  justify-self: start;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef0f6;
  color: #555a75;

  &--owner {
    background: rgba(62, 207, 180, 0.15);
    color: $green;
  }

  &--admin {
    background: rgba(45, 43, 85, 0.08);
    color: $primary;
  }
}

// ── Botones secundarios ──
.team-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1.5px solid $border;
  border-radius: 10px;
  background: #fff;
  color: $primary;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: $accent;
  }

  &--accent {
    border-color: #3ecfb4;
    background: rgba(62, 207, 180, 0.12);
    color: #1d9e75;
    text-decoration: none;
  }

  &--danger {
    color: #e5484d;

    &:hover {
      border-color: #e5484d;
    }
  }
}

.team-icon-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $muted;
  cursor: pointer;

  &:hover {
    background: #eef0f6;
    color: $primary;
  }
}

// ── Diálogo de invitación (pantalla completa) ──
::v-deep .team-dialog {
  .el-dialog__body {
    padding: 0;
  }
}

.team-dialog__header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid $border;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 24px;
}

.team-dialog__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: $primary;
}

.team-dialog__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.team-dialog__close {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $muted;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    background: #eef0f6;
    color: $primary;
  }
}

.team-dialog__body {
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 16px;
}

.team-dialog__help {
  margin: 6px 0 0;
  color: $muted;
  font-size: 13px;
  line-height: 1.5;
}

.team-result {
  text-align: center;
  padding: 24px 0;

  &__icon {
    font-size: 44px;
    color: $green;
  }

  &.is-warning &__icon {
    color: #f5a623;
  }

  &__message {
    margin: 14px 0 20px;
    color: $primary;
    font-size: 15px;
  }

  &__link {
    display: flex;
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .team-row {
    grid-template-columns: 1fr auto;

    .team-row__meta {
      display: none;
    }

    .team-role {
      justify-self: end;
    }

    &__actions {
      grid-column: 2;
    }

    &__buttons {
      grid-column: 1 / -1;
      justify-content: flex-start;
    }
  }

  .team-dialog__header {
    padding: 12px 16px;
  }
}
</style>
